import type { PrescriptionPrintPayload } from '@/components/prescriptions/utils/buildPrescriptionPrintPayload'
import type { GACourse } from '@/components/prescriptions/utils/prescriptionGAEvents'
import {
	getGAPageContext,
	PRESCRIPTION_GA_EVENT,
} from '@/components/prescriptions/utils/prescriptionGAEvents'
import { formatPrescriptionsListLabel } from '@/components/prescriptions/utils/prescriptionSelectionRules'
import { sendPrescriptionGAEvent } from '@/components/prescriptions/utils/sendPrescriptionGAEvent'
import { getPrescriptionSelectionDocs } from '@/lib/getPrescriptionDocsFile'
import type { PrescriptionSelectionListMeta } from '@/types/fetchPrescriptionSelection'
import { Icon } from '@iconify/react'
import JsFileDownloader from 'js-file-downloader'
import { Button } from 'nsw-ds-react'
import { useEffect, useRef, useState } from 'react'

export interface PrescriptionDownloadSelectionButtonProps {
	listcodename: string
	listMeta: PrescriptionSelectionListMeta | undefined
	// Nested GA payload (one entry per complete course) for the download event.
	gaCourses: GACourse[]
	printPayload: PrescriptionPrintPayload
	disabled: boolean
	preview?: boolean
}

export function PrescriptionDownloadSelectionButton({
	listcodename,
	listMeta,
	gaCourses,
	printPayload,
	disabled,
	preview = false,
}: PrescriptionDownloadSelectionButtonProps) {
	const [isDownloading, setIsDownloading] = useState(false)
	const refDownloadAbortController = useRef<AbortController | null>(null)

	useEffect(() => {
		return () => {
			refDownloadAbortController.current?.abort()
		}
	}, [])

	const handleDownload = async () => {
		if (isDownloading || disabled || !listcodename) return

		const prescriptionsListLabel = formatPrescriptionsListLabel(
			listMeta?.title,
			listMeta?.years,
		)

		// v2: push ONE nested event with all complete courses; GTM splits it into
		// per-selection events.
		sendPrescriptionGAEvent({
			event: PRESCRIPTION_GA_EVENT.tool1Download,
			...getGAPageContext(),
			prescriptions_list: prescriptionsListLabel,
			courses: gaCourses,
		})

		refDownloadAbortController.current?.abort()
		const controller = new AbortController()
		refDownloadAbortController.current = controller
		setIsDownloading(true)
		try {
			const { ok, output, message } = await getPrescriptionSelectionDocs(
				{ payload: printPayload, isPreviewMode: preview },
				controller.signal,
			)
			if (controller.signal.aborted) return
			if (ok && output && output.code === 200 && output.path) {
				new JsFileDownloader({
					url: output.path,
					filename: output.name ?? undefined,
					autoStart: true,
					forceDesktopMode: true,
				})
			} else if (!controller.signal.aborted) {
				console.warn(
					'Prescription selection download failed:',
					output?.message || message,
				)
			}
		} catch (err) {
			if (!controller.signal.aborted) {
				console.warn('Prescription selection download error:', err)
			}
		} finally {
			if (refDownloadAbortController.current === controller) {
				setIsDownloading(false)
			}
		}
	}

	return (
		<Button
			type="button"
			style="dark"
			onClick={handleDownload}
			disabled={isDownloading || disabled || !listcodename}
			className="flex items-center gap-2 shrink-0 !h-12"
			data-testid="prescription-selection-download"
		>
			<span>{isDownloading ? 'Preparing…' : 'Download selection'}</span>
			<Icon icon="bxs:download" width={24} height={24} />
		</Button>
	)
}

export default PrescriptionDownloadSelectionButton
